import { useState } from "react";
import {
  useRequestUploadUrlMutation,
  useConvertUploadedFileMutation,
  useRegisterAssetMutation,
} from "../redux/api";
import type { UploadEntityType } from "../redux/api";

// What `convertUploadedFile` actually turns each accepted source type into
// (see services/src/common/aws/s3/media-convert.ts) — used to register the
// Gallery asset under the mime type the *converted* key really is, not the
// mime type of the file the user originally picked.
const convertedMimeType = (sourceMimeType: string): string =>
  sourceMimeType.startsWith("video/") ? "application/vnd.apple.mpegurl" : "image/png";

const ALLOWED_MIME_TYPES = [
  "image/heic",
  "image/heif",
  "video/mp4",
  "video/quicktime",
  "video/webm",
];
const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100MB — mirrors the backend's video ceiling (see services/src/common/aws/s3/key.ts)

export interface UploadedMedia {
  /** The converted key (HLS "index.m3u8" for video, ".png" for HEIC/HEIF) — store this, not the raw upload key. */
  key: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
}

// Sibling to useImageUpload for the file types that need a post-upload
// conversion step (see services/src/common/aws/s3/media-convert.ts): upload
// the raw file to S3 same as any other upload, then trigger conversion and
// return the *converted* key — nothing reads the raw HEIC/video key back.
export const useMediaUpload = (entityType: UploadEntityType, entityId: string) => {
  const [requestUploadUrl] = useRequestUploadUrlMutation();
  const [convertUploadedFile] = useConvertUploadedFileMutation();
  const [registerAsset] = useRegisterAssetMutation();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (file: File): Promise<UploadedMedia | null> => {
    setError(null);

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      setError("Only HEIC/HEIF images or MP4/MOV/WebM video files are allowed.");
      return null;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File is too large — maximum size is ${MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB.`);
      return null;
    }

    setUploading(true);
    try {
      const { uploadUrl, key: rawKey } = await requestUploadUrl({
        entityType,
        entityId,
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
      }).unwrap();

      const putResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error(`Upload to storage failed (${putResponse.status}).`);
      }

      const { key } = await convertUploadedFile({ key: rawKey, mimeType: file.type }).unwrap();
      const mimeType = convertedMimeType(file.type);

      // Best-effort, same reasoning as useImageUpload — the upload+conversion
      // already succeeded, so a registration failure here shouldn't fail the
      // whole operation.
      try {
        await registerAsset({ key, fileName: file.name, mimeType, fileSize: file.size }).unwrap();
      } catch {
        // Swallow — see comment above.
      }

      return { key, fileName: file.name, mimeType, fileSize: file.size };
    } catch (err) {
      setError((err as { message?: string })?.message ?? "Failed to upload file.");
      return null;
    } finally {
      setUploading(false);
    }
  };

  return { upload, uploading, error };
};
